import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";

//importing custom modules
import { Button } from "../../components";
import { newUserSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import AdminFormFields, { AdminFormValues } from "./AdminFormFields";

// importing styles
import style from "./admin.module.css";
import { colors } from "../../theme/colors";

// importing api
import {
  useUpdateUserMutation,
  useGetUserDetailsQuery,
} from "../../services/api";
import { LabelBox } from "../../components";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../../utils/constants";

/**
 * Renders the edit-admin form and submits updates for an existing admin user.
 *
 * Notes:
 * - Requires a valid admin id in the route params.
 * - Email is read-only and not submitted as part of updates.
 * - TODO: Confirm optional fields and update constraints for admin edits.
 */
const EditAdmin = () => {
  const { id } = useParams();
  const navigation = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  // Get Admin Details mutation hook
  const {
    data: adminDetails,
    isLoading,
    isFetching,
  } = useGetUserDetailsQuery(id ?? "", { skip: !id });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const isSuperAdminUser =
    (adminDetails?.user_role_id ?? 0) === DEFAULT_SUPER_ADMIN_ROLE_ID;

  useEffect(() => {
    if (!id) {
      navigation(-1);
    }
  }, [id, navigation]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<AdminFormValues>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  // Hydrate form defaults once the admin record loads.
  useEffect(() => {
    if (!isEmpty(adminDetails)) {
      reset({
        prefix:  (adminDetails)?.prefix ?? "",
        given_name:  (adminDetails)?.given_name ?? "",
        surname: (adminDetails)?.surname ?? "",
        mail: (adminDetails)?.mail ?? "",
        phone: (adminDetails)?.phone ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminDetails]);

  // Protect update calls from invalid route ids to avoid NaN payloads.
  const onSubmit = async (values: AdminFormValues) => {
    const parsedId = Number(id);
    if (!id || Number.isNaN(parsedId)) {
      setMessage({
        type: "error",
        text: "Invalid admin id. Please refresh and try again.",
      });
      return;
    }
    if (isSuperAdminUser && !values.phone?.trim()) {
      setMessage({
        type: "error",
        text: "Phone number is required for Super Admin.",
      });
      return;
    }
    try {
      setLoading(true);
      let payload: EditUserPayload = {
        id: parsedId,
        given_name: values?.given_name,
        surname: values?.surname,
        phone: values?.phone,
      };

      // Only send optional fields when present to avoid overwriting with blanks.
      if (values.prefix && values.prefix !== "") {
        payload.prefix = values.prefix;
      }

      await updateUser({ ...payload }).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Admin updated successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!id) {
    return null;
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography className={style.detailHeading}>Admin Details</Typography>
          <Box className={style.flexTop} sx={{ marginTop: "20px" }}>
            <AdminFormFields
              register={register}
              errors={errors}
              prefixValue={watch("prefix") ?? ""}
              disableEmail={true}
              phoneRequired={isSuperAdminUser}
            />
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                }}
                variant="outlined"
                onClick={() => navigation(-1)}
                label="Cancel"
              />
              <Button
                loading={loading}
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label="Save"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditAdmin;
